<script setup>
import { computed, inject, ref } from 'vue';
import { siteLocation } from '../siteLocation.js';
import { hasLink, isExternal, styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const TONES = ['closed', 'caution', 'info'];
const tone = computed(() => (TONES.includes(props.config.tone) ? props.config.tone : 'info'));

// `auto` reads the live trail rows: the first closed trail, its name
// and the crew's own note. Nothing closed means nothing to say, and
// the banner renders no markup at all rather than an empty dark strip
// — a notice bar with nothing in it reads as a broken page.
const autoTrail = computed(() => (props.site_data?.trails || []).find((t) => t.bucket === 'closed') || null);
const isAuto = computed(() => props.config.source === 'auto');

const text = computed(() => {
    if (!isAuto.value) return String(props.config.text ?? '').trim();
    if (!autoTrail.value) return '';
    const note = String(autoTrail.value.note ?? '').trim();
    return `${autoTrail.value.name} is closed.${note ? ` ${note}` : ''}`;
});
const tag = computed(() => {
    const own = String(props.config.tag ?? '').trim();
    if (own) return own;
    return tone.value === 'caution' ? 'Caution' : (tone.value === 'closed' ? 'Closure' : 'Notice');
});

// One dismissal per page per message. sessionStorage rather than
// localStorage: a closure the visitor waved away this morning is
// worth showing again tomorrow, and the note usually changes with the
// closure anyway, which the key picks up.
const storageKey = computed(() => {
    const path = siteLocation().pathname;
    return `tiq-th-alert:${path}:${props.config.anchor_id || ''}:${text.value.slice(0, 80)}`;
});
function wasDismissed() {
    try {
        return typeof window !== 'undefined' && window.sessionStorage.getItem(storageKey.value) === '1';
    } catch {
        // Private window, blocked site data. The banner shows, which
        // is the safe direction for a closure notice.
        return false;
    }
}
const dismissed = ref(wasDismissed());
function dismiss() {
    dismissed.value = true;
    try {
        window.sessionStorage.setItem(storageKey.value, '1');
    } catch {
        // Nothing to remember it with. It stays hidden for this view.
    }
}

// In the editor a hidden bar still needs a place to click.
const editing = inject('tiqEditorCanvas', false);
const show = computed(() => !!text.value && !dismissed.value);
const link = computed(() => props.config.link || {});
const dismissible = computed(() => props.config.dismissible !== false);
</script>

<template>
    <section v-if="show"
             :id="config.anchor_id || null"
             class="th-block th-dark th-alert"
             :style="styleVars(config)">
        <div class="th-wrap th-alert__inner">
            <span class="th-tag th-alert__tag" :class="`th-tag--${tone}`">{{ tag }}</span>
            <span class="th-alert__text">{{ text }}</span>
            <a v-if="hasLink(link)"
               class="th-alert__link"
               :href="link.href || '#'"
               :target="isExternal(link.href) ? '_blank' : null"
               :rel="isExternal(link.href) ? 'noopener noreferrer' : null">{{ link.label }}</a>
            <button v-if="dismissible" type="button" class="th-alert__close" aria-label="Hide this notice" @click="dismiss">
                &#10005;
            </button>
        </div>
    </section>
    <p v-else-if="editing" class="th-alert__hidden-note">
        {{ isAuto ? 'Nothing is closed right now, so visitors do not see this bar.' : 'Type a message to show this bar.' }}
    </p>
</template>

<style scoped>
.th-alert__hidden-note { margin: 0; padding: 10px 24px; font-size: 13px; color: #6b6f68; background: #eceae4; }
.th-alert__inner {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    padding-top: 12px;
    padding-bottom: 12px;
}
.th-alert__tag { flex: none; }
/* The info tone has no stock chip: the design's neutral chip is light
   on light, which is invisible on the dark band. */
.th-alert__tag.th-tag--info { background: rgba(255, 255, 255, .16); color: #fff; }
.th-alert__text { flex: 1; min-width: 220px; font-size: 15px; }
.th-alert__link { font-weight: 600; font-size: 15px; text-decoration: underline; }
.th-alert__close {
    flex: none;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, .34);
    background: none;
    color: inherit;
    font-size: 13px;
    line-height: 1;
}
.th-alert__close:hover { background: rgba(255, 255, 255, .12); }
</style>
